import { createResource, onCleanup } from "solid-js";
import { getTenantQuota } from "../api/tenants";
import type { BoardEvent, TenantId, TenantQuotaStatus } from "../api/types";

const REFETCH_JITTER_MS = 2000;

export interface UseQuotaStatusResult {
  status: () => TenantQuotaStatus | undefined;
  handleWebSocketEvent: (event: BoardEvent) => void;
  refetch: () => void;
}

export function useQuotaStatus(
  tenantId: () => TenantId | undefined,
  jitterMs: number = REFETCH_JITTER_MS,
): UseQuotaStatusResult {
  // `createResource` keyed on the tenant: a falsy key skips the fetch (D3).
  const [quota, { refetch: refetchResource }] = createResource(
    tenantId,
    async (id) => {
      try {
        return await getTenantQuota(id);
      } catch {
        return undefined; // D5: the banner is an enrichment; any failure renders nothing
      }
    },
  );

  // A resolved response can still be for the tenant we've since switched
  // away from (a resource in flight when tenantId changes); only surface it
  // once it matches the tenant currently selected, rather than gating on
  // `quota.state === "ready"`, which would also hide it during a refetch.
  const status = () => {
    const latest = quota.latest;
    return latest && latest.tenant_id === tenantId() ? latest : undefined;
  };

  // One pending timer is the dedupe for the N copies a multi-project member
  // receives (787 D3) and the spread for the refetch herd (788 F-02).
  let pending: ReturnType<typeof setTimeout> | undefined;
  const scheduleRefetch = () => {
    if (pending) return;
    pending = setTimeout(() => {
      pending = undefined;
      void refetchResource();
    }, Math.random() * jitterMs);
  };
  onCleanup(() => clearTimeout(pending));

  const handleWebSocketEvent = (event: BoardEvent) => {
    if (event.type !== "QuotaLevelChanged") return;
    if (event.payload.tenant_id !== tenantId()) return;
    scheduleRefetch();
  };

  const refetch = () => {
    if (!tenantId()) return;
    clearTimeout(pending);
    pending = undefined;
    void refetchResource();
  };

  return { status, handleWebSocketEvent, refetch };
}
